import React, { useEffect, useRef } from "react";

export default function Step2Form({
  form,
  handleChange,
  handleFinalize,
  handleDownloadPDF,
  loading,
  pdfLibReady,
  price,
  indemnity,
  formatCurrency,
  goBack,
  t,
  common,
}) {
  const birthCountryRef = useRef(null);
  const residenceRef = useRef(null);
  const capRef = useRef(null);

  useEffect(() => {
    if (!window.google?.maps?.places) return;

    const countryAutocomplete = new window.google.maps.places.Autocomplete(
      birthCountryRef.current,
      { types: ["(regions)"], fields: ["address_components", "formatted_address"] }
    );

    const residenceAutocomplete = new window.google.maps.places.Autocomplete(
      residenceRef.current,
      { types: ["address"], componentRestrictions: { country: "it" }, fields: ["formatted_address"] }
    );

    const capAutocomplete = new window.google.maps.places.Autocomplete(capRef.current, {
      types: ["(regions)"],
      componentRestrictions: { country: "it" },
      fields: ["address_components", "formatted_address"],
    });

    countryAutocomplete.addListener("place_changed", () => {
      const place = countryAutocomplete.getPlace();
      const country =
        place.address_components?.find((c) => c.types.includes("country"))?.long_name ||
        place.formatted_address;
      handleChange({ target: { name: "birth_country", value: country } });
    });

    residenceAutocomplete.addListener("place_changed", () => {
      const place = residenceAutocomplete.getPlace();
      handleChange({ target: { name: "residence", value: place.formatted_address || "" } });
    });

    capAutocomplete.addListener("place_changed", () => {
      const place = capAutocomplete.getPlace();
      const postalCode =
        place.address_components?.find((c) => c.types.includes("postal_code"))?.long_name || "";
      handleChange({ target: { name: "cap", value: postalCode || place.formatted_address } });
    });

    return () => {
      window.google?.maps?.event?.clearInstanceListeners(countryAutocomplete);
      window.google?.maps?.event?.clearInstanceListeners(residenceAutocomplete);
      window.google?.maps?.event?.clearInstanceListeners(capAutocomplete);
    };
  }, []);

  return (
    <form onSubmit={handleFinalize} className="configurator-form fade-in">
      {/* DATI PERSONALI */}
      <h3 className="subtitle">👤 {t.personalData}</h3>

      <div className="two-cols">
        <div className="col">
          <label>{t.supplierCode}</label>
          <input
            type="text"
            name="supplier_code"
            value={form.supplier_code || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col">
          <label>{t.weddingOf}</label>
          <input
            type="text"
            name="wedding_of"
            value={form.wedding_of || ""}
            onChange={handleChange}
            placeholder={t.weddingOfPlaceholder}
            required
          />
        </div>
      </div>

      <div className="two-cols">
        <div className="col">
          <label>{t.firstName}</label>
          <input type="text" name="first_name" value={form.first_name} onChange={handleChange} required />
        </div>
        <div className="col">
          <label>{t.lastName}</label>
          <input type="text" name="last_name" value={form.last_name} onChange={handleChange} required />
        </div>
      </div>

      <div className="two-cols">
        <div className="col">
          <label>Email</label>
          <input type="email" name="email" value={form.email} onChange={handleChange} required />
        </div>
        <div className="col">
          <label>{t.phone}</label>
          <input type="tel" name="phone" value={form.phone} onChange={handleChange} required />
        </div>
      </div>

      <div className="two-cols">
        <div className="col">
          <label>{t.fiscalCode}</label>
          <input type="text" name="fiscal_code" value={form.fiscal_code || ""} onChange={handleChange} required />
        </div>
        <div className="col">
          <label>{t.gender}</label>
          <select name="gender" value={form.gender || ""} onChange={handleChange} required>
            <option value="">{common.select}</option>
            <option value="M">{common.male}</option>
            <option value="F">{common.female}</option>
          </select>
        </div>
      </div>

      <div className="two-cols">
        <div className="col">
          <label>{t.birthDate}</label>
          <input type="date" name="birth_date" value={form.birth_date || ""} onChange={handleChange} required />
        </div>
        <div className="col">
          <label>{t.birthPlace}</label>
          <input
            type="text"
            name="birth_country"
            placeholder={t.birthPlacePlaceholder}
            ref={birthCountryRef}
            value={form.birth_country || ""}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* RESIDENZA */}
      <h3 className="subtitle">🏠 {t.residence}</h3>
      <div className="two-cols">
        <div className="col">
          <label>{t.fullAddress}</label>
          <input
            type="text"
            name="residence"
            placeholder={t.residencePlaceholder}
            ref={residenceRef}
            value={form.residence || ""}
            onChange={handleChange}
            required
          />
        </div>
        <div className="col">
          <label>CAP</label>
          <input
            type="text"
            name="cap"
            placeholder={t.capPlaceholder}
            ref={capRef}
            value={form.cap || ""}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      {/* DICHIARAZIONI */}
      <div className="checkbox-wrapper">
        <label className="checkbox-item">
          <input
            type="checkbox"
            name="confirm_privacy"
            checked={form.confirm_privacy || false}
            onChange={handleChange}
            required
          />
          <span>
            {t.privacyBefore}{" "}
            <a
              href="https://wedly.it/wp-content/uploads/2025/07/WEDLY_informativa-protezione-dati-2.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.privacyLink}
            </a>.
          </span>
        </label>

        <label className="checkbox-item">
          <input
            type="checkbox"
            name="confirm_rain_protection"
            checked={form.confirm_rain_protection || false}
            onChange={handleChange}
            required
          />
          <span>
            {t.rainConfirm}
          </span>
        </label>
      </div>

      {/* AZIONI */}
      <div className="button-group">
        <button type="button" className="back-btn" onClick={goBack}>
          {t.back}
        </button>

        <button type="submit" className="update-btn" disabled={loading}>
          {loading ? t.submitLoading : t.submit}
        </button>
      </div>

      <button
        type="button"
        className="update-btn alt"
        onClick={handleDownloadPDF}
        disabled={!pdfLibReady}
      >
        📄 {t.downloadPdf}
      </button>
    </form>
  );
}
